'use client';

import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { DataViewToggle, useDataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SortControl } from '@/components/sort-control';
import { StickingCard } from './sticking-card';
import { StickingCompactRow } from './sticking-compact-row';
import { useTranslations } from 'next-intl';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@/lib/table-features';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState,
  useQueryStates
} from 'nuqs';
import type { ExtendedColumnSort } from '@/types/data-table';
import { getSortingStateParser } from '@/lib/parsers';
import { buildDynamicFormColumns } from '@/lib/build-dynamic-form-columns';
import { Camera, QrCode, Eye, ScanLine, FileText, Check } from 'lucide-react';
import { QrScanDialog } from '@/components/qr-scan-dialog';

import { toast } from 'sonner';
import { useDataTable } from '@/hooks/use-data-table';
import { useFilteredColumns } from '@/hooks/use-filtered-columns';
import { useSelectAllPages } from '@/hooks/use-select-all-pages';
import { useHashDialog, useHashParamDialog } from '@/hooks/use-hash-dialog';
import { useRouter } from '@/i18n/routing';
import { cn } from '@/lib/utils';
import { IS_QR_SCANNER_ENABLED } from '@/lib/feature-flags';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { ListingSkeleton } from '@/components/listing-skeleton';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import {
  HighlightMatch,
  getColumnSearchTerms
} from '@/components/highlight-match';
import { SelectAllBanner } from '@/components/select-all-banner';
import {
  registrationQueries,
  registrationKeys,
  type Registration,
  type RegistrationsListParams
} from '@/features/registration';
import { dynamicFormQueries, type DynamicForm } from '@/features/dynamic-forms';

import { stickingApi } from '../services/sticking.api';
import { stickingKeys } from '../services/sticking.queries';
import { StickingTableActionBar } from './sticking-table-action-bar';
import { QrDataDialog } from './qr-data-dialog';
import { useHardwareScanner } from '@/hooks/use-hardware-scanner';
import { useQrNavigateSearch } from '@/hooks/use-qr-navigate-search';
import {
  InlinePageInput,
  InlineDiffCell,
  InlineSaveButton,
  type InlineEdit,
  type DiffNotifier
} from './sticking-inline-edit';
// import { StickingActionsDialog } from './sticking-actions-dialog';
import { STICKING_HASH } from '../constants';
// import { ListChecks } from 'lucide-react';
import {
  SubStatusCell,
  getEffectiveStage,
  getEffectiveStagePillStyle
} from '@/features/registration';
import { stageFilterOptions, useWorkflowStages } from '@/features/workflow';

// ─── QR Dialog wrapper that fetches registration by ID if not in page ──────

function QrDataDialogWithFetch({
  registrationId,
  open,
  onOpenChange,
  cachedRegistrations
}: {
  registrationId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cachedRegistrations: Registration[];
}) {
  const cached = cachedRegistrations.find((c) => c.id === registrationId);

  const { data } = useQuery({
    ...registrationQueries.detail(registrationId),
    enabled: open && !cached
  });

  const registration = cached ?? (data?.data as Registration | undefined);

  if (!registration) return null;

  return (
    <QrDataDialog
      open={open}
      onOpenChange={onOpenChange}
      registration={registration}
    />
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function StickingListing() {
  const pipeline = useWorkflowStages();
  const t = useTranslations('sticking');
  const tc = useTranslations('common');
  const tReg = useTranslations('registration');
  const router = useRouter();
  const queryClient = useQueryClient();
  const activeStage = 'sticking' as const;
  const [scannerActive, setScannerActive] = useState(true);
  const [hwScan, setHwScan] = useState<{ value: string; key: number } | null>(
    null
  );
  // Overlay open-state lives in the URL hash so overlays are deep-linkable,
  // survive refresh, and close on browser Back.
  const {
    open: actionsOpen,
    param: actionsId,
    openWith: openActions,
    onOpenChange: closeActions
  } = useHashParamDialog(STICKING_HASH.actions);
  const {
    open: qrDialogOpen,
    param: qrDialogTargetIdRaw,
    openWith: openQrDialog,
    onOpenChange: closeQrDialog
  } = useHashParamDialog(STICKING_HASH.qr);
  const qrDialogTargetId = qrDialogTargetIdRaw
    ? Number(qrDialogTargetIdRaw)
    : null;
  const { open: scanDialogOpen, onOpenChange: setScanDialogOpen } =
    useHashDialog(STICKING_HASH.scan);

  const [view, setView] = useDataView('sticking');

  const { results: hwScanResults, isFetching: hwScanFetching } =
    useQrNavigateSearch({
      searchInput: hwScan?.value ?? '',
      stage: activeStage,
      enabled: !!hwScan
    });

  useEffect(() => {
    if (!hwScan || hwScanFetching) return;
    if (hwScanResults.length > 0) {
      openQrDialog(String(hwScanResults[0].id));
    } else {
      toast.error(t('notFound'));
    }
  }, [hwScanResults, hwScanFetching, hwScan, t, openQrDialog]);

  useHardwareScanner({
    onScan: (value) => setHwScan({ value, key: Date.now() }),
    enabled: scannerActive && !qrDialogOpen && !scanDialogOpen
  });

  // Ref-based inline edit state — no re-renders on typing
  const inlineEditsRef = useRef(new Map<number, InlineEdit>());
  // Callback registry so input cells can notify diff cells
  const diffNotifiersRef = useRef<DiffNotifier>(new Map());

  const inlineSaveMutation = useMutation({
    mutationFn: ({
      registrationId,
      startPage,
      endPage
    }: {
      registrationId: number;
      startPage: number;
      endPage: number;
    }) =>
      stickingApi.createPrintBatch(registrationId, {
        start_page: startPage,
        page_count: endPage - startPage + 1
      }),
    meta: {
      operationType: 'create',
      fallbackSuccessMessage: t('printBatchCreated')
    },
    onSuccess: (_data, variables) => {
      inlineEditsRef.current.delete(variables.registrationId);
      diffNotifiersRef.current.delete(variables.registrationId);
      queryClient.invalidateQueries({
        queryKey: stickingKeys.printBatches()
      });
      queryClient.invalidateQueries({
        queryKey: stickingKeys.metrics()
      });
      queryClient.invalidateQueries({
        queryKey: registrationKeys.lists()
      });
    }
  });

  const handleInlineSave = useCallback(
    (regId: number, startPage: number, endPage: number) => {
      inlineSaveMutation.mutate({
        registrationId: regId,
        startPage,
        endPage
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // ─── Dynamic form selector ──────────────────────────────────────────
  const { data: activeTemplate } = useQuery(
    dynamicFormQueries.activeByType('registration')
  );
  const { data: allFormsData } = useQuery(
    dynamicFormQueries.list({
      type: 'registration',
      status: 'active',
      per_page: 100
    })
  );
  const allForms = (allFormsData?.data ?? []) as DynamicForm[];

  const [urlFormId, setUrlFormId] = useQueryState(
    'dynamic_form_id',
    parseAsString.withDefault('')
  );
  const activeFormId = activeTemplate?.data?.id;
  const isAllSelected = urlFormId === 'all';

  const displayFormId = isAllSelected
    ? activeFormId
    : urlFormId
      ? Number(urlFormId)
      : activeFormId;

  const apiFormId = isAllSelected
    ? undefined
    : urlFormId
      ? Number(urlFormId)
      : activeFormId;

  const { data: selectedFormData } = useQuery({
    ...dynamicFormQueries.detail(displayFormId ?? 0),
    enabled: !!displayFormId && displayFormId !== activeFormId
  });

  const selectedForm =
    displayFormId === activeFormId
      ? activeTemplate?.data
      : selectedFormData?.data;
  const dynamicFormFields = useMemo(
    () => selectedForm?.fields ?? [],
    [selectedForm?.fields]
  );
  const dynamicColumns = useMemo(
    () => buildDynamicFormColumns(dynamicFormFields, { group: 'page_indexes' }),
    [dynamicFormFields]
  );
  const toolbarKey = useMemo(
    () => dynamicColumns.map((c) => c.id).join(',') || 'base',
    [dynamicColumns]
  );

  const selectionFields = useMemo(
    () => dynamicFormFields.filter((f) => f.type === 'selection_list'),
    [dynamicFormFields]
  );
  const dynamicFieldParsers = useMemo(
    () =>
      Object.fromEntries(
        selectionFields.map((f) => [
          `form_field_${f.id}`,
          parseAsArrayOf(parseAsString, ',').withDefault([])
        ])
      ),
    [selectionFields]
  );
  const [dynamicFieldFilters] = useQueryStates(dynamicFieldParsers);

  const textFields = useMemo(
    () =>
      dynamicFormFields.filter(
        (f) =>
          f.type === 'short_text' ||
          f.type === 'long_text' ||
          f.type === 'phone' ||
          f.type === 'email'
      ),
    [dynamicFormFields]
  );

  const dateFields = useMemo(
    () => dynamicFormFields.filter((f) => f.type === 'date'),
    [dynamicFormFields]
  );
  const dynamicDateParsers = useMemo(
    () =>
      Object.fromEntries(
        dateFields.map((f) => [
          `form_field_${f.id}`,
          parseAsString.withDefault('')
        ])
      ),
    [dateFields]
  );
  const [dynamicDateFilters] = useQueryStates(dynamicDateParsers);

  const searchPlaceholder =
    textFields.length > 0
      ? `${t('searchPlaceholder')}, ${textFields.map((f) => f.label).join(', ')}...`
      : `${t('searchPlaceholder')}...`;

  // Helper to ensure a row's ref entry exists
  const ensureEdit = (regId: number) => {
    if (!inlineEditsRef.current.has(regId)) {
      inlineEditsRef.current.set(regId, { startPage: 1, endPage: 1 });
    }
  };

  const columns: ColumnDef<Registration>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
        />
      ),
      enableSorting: false,
      enableHiding: false,
      size: 40
    },
    {
      id: 'container_number',
      accessorKey: 'container_number',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('containerNumber')} />
      ),
      cell: ({ row, column }) => (
        <button
          type='button'
          className='text-primary flex items-center gap-1 font-medium hover:underline'
          onClick={() =>
            router.push(
              `/dashboard/containers/${row.original.id}?stage=sticking`
            )
          }
        >
          <span className='text-primary'>↗</span>
          <HighlightMatch
            text={row.original.container_number}
            query={getColumnSearchTerms(column)}
          />
        </button>
      ),
      enableColumnFilter: true,
      meta: {
        label: searchPlaceholder,
        variant: 'text' as const,
        placeholder: searchPlaceholder,
        permission: 'page_indexes.column.containerNumber'
      }
    },
    // {
    //   id: 'template_name',
    //   header: () => <span className='text-xs'>{t('usedTemplate')}</span>,
    //   cell: ({ row }) => (
    //     <span className='text-muted-foreground text-sm'>
    //       {row.original.dynamic_form?.name ?? '—'}
    //     </span>
    //   ),
    //   enableSorting: false,
    //   enableHiding: false,
    //   size: 140,
    //   meta: { label: t('usedTemplate') }
    // },
    ...dynamicColumns,
    {
      id: 'batch_start_page',
      header: () => <div className='text-center text-xs'>{t('firstPage')}</div>,
      meta: {
        label: t('firstPage'),
        permission: 'page_indexes.column.firstPage'
      },
      cell: ({ row }) => {
        const reg = row.original;
        if (reg.latest_batch_id) {
          return (
            <div className='text-center text-sm'>
              {reg.latest_batch_start_page}
            </div>
          );
        }
        ensureEdit(reg.id);
        return (
          <InlinePageInput
            key={`start-${reg.id}`}
            registrationId={reg.id}
            field='startPage'
            defaultValue={inlineEditsRef.current.get(reg.id)?.startPage ?? 1}
            min={1}
            editsRef={inlineEditsRef}
            notifiersRef={diffNotifiersRef}
          />
        );
      },
      enableSorting: false,
      size: 80
    },
    {
      id: 'batch_end_page',
      header: () => (
        <div className='text-center text-xs'>{t('lastPageCol')}</div>
      ),
      meta: {
        label: t('lastPageCol'),
        permission: 'page_indexes.column.lastPageCol'
      },
      cell: ({ row }) => {
        const reg = row.original;
        if (reg.latest_batch_id) {
          return (
            <div className='flex items-center justify-center gap-1.5'>
              <span className='text-sm'>{reg.latest_batch_end_page}</span>
              <Badge
                variant='secondary'
                className='bg-green-100 px-1.5 py-0 text-[10px] text-green-800 dark:bg-green-900 dark:text-green-200'
              >
                <Check className='me-0.5 h-2.5 w-2.5' />
                {t('saved')}
              </Badge>
            </div>
          );
        }
        ensureEdit(reg.id);
        return (
          <InlinePageInput
            key={`end-${reg.id}`}
            registrationId={reg.id}
            field='endPage'
            defaultValue={inlineEditsRef.current.get(reg.id)?.endPage ?? 1}
            min={1}
            editsRef={inlineEditsRef}
            notifiersRef={diffNotifiersRef}
          />
        );
      },
      enableSorting: false,
      size: 100
    },
    {
      id: 'batch_diff',
      header: () => (
        <div className='text-center text-xs'>{t('totalSheets')}</div>
      ),
      meta: {
        label: t('totalSheets'),
        permission: 'page_indexes.column.totalSheets'
      },
      cell: ({ row }) => {
        const reg = row.original;
        if (reg.latest_batch_id) {
          const currentSheets =
            (reg.latest_batch_end_page ?? 0) -
            (reg.latest_batch_start_page ?? 0) +
            1;
          const originalSheets =
            (reg.latest_batch_original_end_page ?? 0) -
            (reg.latest_batch_original_start_page ?? 0) +
            1;
          const hasOriginal = reg.latest_batch_original_end_page != null;
          const diff = hasOriginal ? currentSheets - originalSheets : 0;
          return (
            <div className='flex items-center justify-center gap-1'>
              <span className='text-sm font-medium'>{currentSheets}</span>
              {diff !== 0 && (
                <Badge
                  variant='secondary'
                  className={cn(
                    'text-[10px] whitespace-nowrap',
                    diff > 0
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  )}
                >
                  {diff > 0 ? '+' : ''}
                  {t('sheetDiff', { count: diff })}
                </Badge>
              )}
            </div>
          );
        }
        ensureEdit(reg.id);
        return (
          <InlineDiffCell
            key={`diff-${reg.id}`}
            registrationId={reg.id}
            editsRef={inlineEditsRef}
            notifiersRef={diffNotifiersRef}
            sheetLabel={t('sheetDiff', { count: '{count}' })}
          />
        );
      },
      enableSorting: false,
      size: 80
    },
    {
      id: 'batch_save',
      header: () => null,
      cell: ({ row }) => {
        const reg = row.original;
        if (reg.latest_batch_id) return null;
        return (
          <InlineSaveButton
            key={`save-${reg.id}`}
            registrationId={reg.id}
            editsRef={inlineEditsRef}
            isPending={inlineSaveMutation.isPending}
            onSave={handleInlineSave}
            label={t('saveBatch')}
          />
        );
      },
      enableSorting: false,
      enableHiding: false,
      size: 40
    },
    {
      id: 'review_status',
      header: tc('reviewStatus'),
      meta: {
        label: tc('reviewStatus'),
        permission: 'page_indexes.column.reviewStatus'
      },
      cell: ({ row }) => {
        const reg = row.original;
        if (reg.rejected_to_stage) {
          const stageLabels: Record<string, string> = {
            registration: tc('stageRegistration'),
            sticking: tc('stageSticking')
          };
          return (
            <div className='flex flex-col gap-1'>
              <Badge
                variant='outline'
                className='border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-300'
                title={reg.last_rejection_reason ?? ''}
              >
                {tc('returnedForCorrection')}
              </Badge>
              <span className='text-muted-foreground text-xs'>
                → {stageLabels[reg.rejected_to_stage] ?? reg.rejected_to_stage}
              </span>
            </div>
          );
        }
        if (reg.review_completed_at) {
          return (
            <Badge
              variant='outline'
              className='border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300'
            >
              {tc('approved')}
            </Badge>
          );
        }
        if (reg.review_started_at || reg.current_stage === 'review') {
          return (
            <Badge
              variant='outline'
              className='border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-800 dark:bg-purple-950 dark:text-purple-300'
            >
              {tc('inReview')}
            </Badge>
          );
        }
        return (
          <span className='text-muted-foreground text-sm'>
            {tc('notReviewed')}
          </span>
        );
      },
      enableSorting: false
    },
    {
      id: 'rejection_count',
      accessorKey: 'rejection_count',
      header: tc('rejectionCount'),
      cell: ({ row }) => {
        const count = row.original.rejection_count;
        if (!count) return null;
        return (
          <Badge
            variant='outline'
            className='border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300'
          >
            {count}
          </Badge>
        );
      },
      enableSorting: false,
      meta: { permission: 'page_indexes.column.rejectionCount' }
    },
    {
      id: 'current_stage',
      accessorKey: 'current_stage',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={tc('currentStage')} />
      ),
      cell: ({ row }) => {
        const effectiveStage = getEffectiveStage(
          row.original.current_stage,
          row.original.stage_status
        );
        const stageLabels: Record<string, string> = {
          registration: tReg('stageRegistration'),
          sticking: tReg('stageSticking'),
          review: tReg('stageReview'),
          ready_for_packing: tReg('stageReadyForPacking'),
          shipping: tReg('stageShipping'),
          reception: tReg('stageReception'),
          sorting: tReg('stageSorting'),
          handover_scanning: tReg('stageHandoverScanning'),
          photo_scanning: tReg('stagePhotoScanning'),
          qr_review: tReg('stageQrReview'),
          warehouse: tReg('stageWarehouse'),
          ocr: tReg('stageOcr'),
          qa1_ocr: tReg('stageQa1Ocr'),
          qa2_ocr: tReg('stageQa2Ocr'),
          qa3_ocr: tReg('stageQa3Ocr'),
          archived: tReg('stageArchived')
        };
        if (effectiveStage === 'archived') return null;
        return (
          <Badge
            variant='secondary'
            className={cn(getEffectiveStagePillStyle(effectiveStage))}
          >
            {stageLabels[effectiveStage] ?? effectiveStage}
          </Badge>
        );
      },
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        label: tc('currentStage'),
        variant: 'multiSelect' as const,
        icon: 'solar:routing-bold-duotone',
        options: stageFilterOptions(
          pipeline.stages,
          (key) => tReg(key),
          'sticking'
        ),
        permission: 'page_indexes.column.currentStage'
      }
    },
    {
      id: 'subStatus',
      accessorKey: 'stage_status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={tReg('subStatus')} />
      ),
      cell: ({ row }) => (
        <SubStatusCell
          stageStatus={row.original.stage_status}
          physicalStage={row.original.physical_stage}
        />
      ),
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        label: tReg('filterStatus'),
        variant: 'multiSelect' as const,
        icon: 'solar:checklist-minimalistic-bold-duotone',
        options: [
          {
            label: tReg('stageStatus.ready_for_numbering'),
            value: 'ready_for_numbering'
          },
          {
            label: tReg('stageStatus.ready_for_printing'),
            value: 'ready_for_printing'
          },
          {
            label: tReg('stageStatus.returned_for_correction'),
            value: 'returned_for_correction'
          }
        ],
        permission: 'page_indexes.column.subStatus'
      }
    },
    {
      id: 'is_rejected',
      accessorFn: (row) => (row.rejected_to_stage !== null ? 'true' : 'false'),
      header: () => null,
      cell: () => null,
      size: 0,
      enableColumnFilter: true,
      enableSorting: false,
      enableHiding: false,
      meta: {
        label: tc('filterRejectionStatus'),
        variant: 'select' as const,
        icon: 'solar:close-circle-bold-duotone',
        options: [
          { label: tc('filterRejected'), value: 'true' },
          { label: tc('filterNotRejected'), value: 'false' }
        ]
      }
    },
    {
      id: 'actions',
      header: () => <div className='text-end'>{t('actions')}</div>,
      cell: ({ row }) => {
        const reg = row.original;
        const hasBatch = !!reg.latest_batch_id;
        return (
          <div className='flex items-center justify-end gap-1'>
            {/*{reg.current_stage === 'sticking' && (
              <Button
                variant='ghost'
                size='icon'
                onClick={() => openActions(String(reg.id))}
                title={t('stickingActions')}
              >
                <ListChecks className='h-4 w-4' />
              </Button>
            )}*/}
            <Button
              variant='ghost'
              size='icon'
              disabled={!hasBatch}
              onClick={() => openQrDialog(String(reg.id))}
              title={t('viewQrData')}
            >
              <QrCode className='h-4 w-4' />
            </Button>
            <Button
              variant='ghost'
              size='icon'
              onClick={() =>
                router.push(`/dashboard/containers/${reg.id}?stage=sticking`)
              }
              title={t('viewContainer')}
            >
              <Eye className='h-4 w-4' />
            </Button>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false
    }
  ];

  const [urlPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [urlPerPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [urlSearch] = useQueryState(
    'container_number',
    parseAsString.withDefault('')
  );
  const [urlStage] = useQueryState(
    'current_stage',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlIsRejected] = useQueryState(
    'is_rejected',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlSubStatus] = useQueryState(
    'subStatus',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlSorting] = useQueryState(
    'sort',
    getSortingStateParser<Registration>().withDefault([
      { id: 'created_at', desc: true }
    ] as ExtendedColumnSort<Registration>[])
  );

  const queryParams = useMemo<RegistrationsListParams>(() => {
    const params: RegistrationsListParams = {
      page: urlPage,
      per_page: urlPerPage,
      current_stage: urlStage.length > 0 ? urlStage.join(',') : activeStage
    };
    if (urlSearch) {
      params.search = urlSearch;
    }
    if (apiFormId) {
      params.dynamic_form_id = apiFormId;
    }
    params.sort_by = urlSorting[0]?.id ?? 'created_at';
    params.sort_direction = urlSorting[0]?.desc ? 'desc' : 'asc';

    if (urlIsRejected.includes('true')) {
      params.is_rejected = true;
    } else if (urlIsRejected.includes('false')) {
      params.is_rejected = false;
    }

    if (urlSubStatus.length > 0) {
      params.stage_status = urlSubStatus.join(',');
    }

    for (const [key, values] of Object.entries(dynamicFieldFilters)) {
      if (Array.isArray(values) && values.length > 0) {
        const fieldId = key.replace('form_field_', '');
        (params as Record<string, unknown>)[
          `form_responses_filter[${fieldId}]`
        ] = values[0];
      }
    }

    if (urlSearch && textFields.length > 0) {
      (params as Record<string, unknown>)['search_text_field_ids'] = textFields
        .map((f) => f.id)
        .join(',');
    }

    for (const [key, value] of Object.entries(dynamicDateFilters)) {
      if (value) {
        const fieldId = key.replace('form_field_', '');
        const parts = String(value).split(',').filter(Boolean);
        const toDateStr = (ts: string) => {
          const d = new Date(Number(ts));
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        };
        const from = parts[0] ? toDateStr(parts[0]) : null;
        const to = parts[1] ? toDateStr(parts[1]) : null;
        if (from)
          (params as Record<string, unknown>)[
            `form_date_filter[${fieldId}][from]`
          ] = from;
        if (to)
          (params as Record<string, unknown>)[
            `form_date_filter[${fieldId}][to]`
          ] = to;
      }
    }

    return params;
  }, [
    urlPage,
    urlPerPage,
    urlSearch,
    urlStage,
    apiFormId,
    urlIsRejected,
    urlSubStatus,
    urlSorting,
    dynamicFieldFilters,
    textFields,
    dynamicDateFilters,
    activeStage
  ]);

  const { data, isPending, isPlaceholderData } = useQuery(
    registrationQueries.list(queryParams)
  );

  const containers = (data?.data ?? []) as Registration[];
  const pageCount = data?.pagination?.totalPages ?? 0;
  const pagination = data?.pagination;

  // Resolve #actions/<id> back to the loaded row (must be on the current page;
  // deep-linking an off-page id simply won't open the dialog).
  const actionsTarget = actionsOpen
    ? (containers.find((c) => String(c.id) === actionsId) ?? null)
    : null;

  const filteredColumns = useFilteredColumns(columns);

  const { table } = useDataTable<Registration>({
    columns: filteredColumns,
    pageKey: 'sticking',
    data: containers,
    pageCount,
    initialState: {
      sorting: [{ id: 'created_at', desc: true }],
      pagination: { pageIndex: 0, pageSize: 10 }
    }
  });

  const selectAll = useSelectAllPages(table, pagination?.total ?? 0);
  const tableState = table.state;

  const sortOptions = useMemo(
    () => [
      { label: t('sortContainerNumber'), value: 'container_number' },
      { label: t('sortCreatedAt'), value: 'created_at' }
    ],
    [t]
  );

  const handleViewQrData = useCallback(
    (reg: Registration) => {
      openQrDialog(String(reg.id));
    },
    [openQrDialog]
  );

  return (
    <div className='space-y-4'>
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex items-center gap-3'>
          <div className='flex items-center gap-2'>
            <FileText className='text-muted-foreground h-4 w-4' />
            <Select
              value={urlFormId || (activeFormId ? String(activeFormId) : '')}
              onValueChange={(v) => {
                if (v === String(activeFormId)) {
                  setUrlFormId(null);
                } else {
                  setUrlFormId(v || null);
                }
              }}
            >
              <SelectTrigger className='h-8 w-auto min-w-[200px] text-xs'>
                <SelectValue placeholder={t('registrationForm')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>{t('allForms')}</SelectItem>
                {allForms.map((form) => (
                  <SelectItem key={form.id} value={String(form.id)}>
                    {form.name}
                    {form.is_default && (
                      <span className='text-muted-foreground ms-1'>
                        ({t('defaultForm')})
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className='flex items-center gap-2'>
          {view !== 'table' && (
            <SortControl
              options={sortOptions}
              sorting={tableState.sorting}
              onSortingChange={(s) => table.setSorting(s)}
            />
          )}
          <DataViewToggle
            value={view}
            onValueChange={setView}
            pageKey='sticking'
          />
          {IS_QR_SCANNER_ENABLED && (
            <>
              <Button
                variant='outline'
                size='sm'
                onClick={() => {
                  setScannerActive((prev) => {
                    if (!prev) (document.activeElement as HTMLElement)?.blur();
                    return !prev;
                  });
                }}
              >
                <span className='relative me-2 flex h-2 w-2 shrink-0'>
                  <span
                    className={cn(
                      'absolute inline-flex h-full w-full rounded-full opacity-75',
                      scannerActive
                        ? 'animate-ping bg-green-400'
                        : 'bg-gray-400'
                    )}
                  />
                  <span
                    className={cn(
                      'relative inline-flex h-2 w-2 rounded-full',
                      scannerActive ? 'bg-green-500' : 'bg-gray-400'
                    )}
                  />
                </span>
                <ScanLine className='me-2 h-4 w-4' />
                {t('scanQR')}
              </Button>
              <Button
                variant='outline'
                size='icon'
                onClick={() => setScanDialogOpen(true)}
                title={tc('scanCamera')}
              >
                <Camera className='h-4 w-4' />
              </Button>
            </>
          )}
        </div>
      </div>

      <DataTableToolbar
        key={toolbarKey}
        table={table}
        viewFilterPermission='page_indexes.view_filter'
      />

      {isPending ? (
        <ListingSkeleton view={view} />
      ) : containers.length === 0 && tableState.columnFilters.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-12 text-center'>
          <FileText className='text-muted-foreground mb-4 h-12 w-12' />
          <p className='text-muted-foreground text-lg'>{t('noContainers')}</p>
        </div>
      ) : view === 'grid' ? (
        <>
          <div
            className={cn(
              'grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {containers.map((reg) => (
              <StickingCard
                key={reg.id}
                registration={reg}
                dynamicFormFields={dynamicFormFields}
                onViewQrData={handleViewQrData}
                onOpenActions={openActions}
                onInlineSave={handleInlineSave}
                isSaving={inlineSaveMutation.isPending}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : view === 'compact' ? (
        <>
          <div
            className={cn(
              'space-y-2',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {containers.map((reg) => (
              <StickingCompactRow
                key={reg.id}
                registration={reg}
                onViewQrData={handleViewQrData}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : (
        <div
          className={cn(
            'flex min-h-[400px] flex-col gap-2',
            isPlaceholderData && 'opacity-60 transition-opacity'
          )}
        >
          <SelectAllBanner
            isAllPagesSelected={selectAll.isAllPagesSelected}
            isCurrentPageFullySelected={selectAll.isCurrentPageFullySelected}
            currentPageCount={containers.length}
            totalCount={pagination?.total ?? 0}
            pageSize={tableState.pagination.pageSize}
            onSelectAllPages={() => selectAll.setAllPagesSelected(true)}
            onClearSelection={() => selectAll.reset()}
          />
          <DataTable
            table={table}
            actionBar={
              <StickingTableActionBar
                table={table}
                isAllPagesSelected={selectAll.isAllPagesSelected}
                totalCount={pagination?.total ?? 0}
              />
            }
          />
        </div>
      )}

      <QrScanDialog
        open={scanDialogOpen}
        onOpenChange={setScanDialogOpen}
        stage={activeStage}
        onNavigate={(id) => openQrDialog(String(id))}
        translationNamespace='sticking'
      />

      {qrDialogTargetId != null && (
        <QrDataDialogWithFetch
          registrationId={qrDialogTargetId}
          open={qrDialogOpen}
          onOpenChange={closeQrDialog}
          cachedRegistrations={containers}
        />
      )}

      {/* Sticking actions dialog — #actions/<id> */}
      {/*{actionsOpen && actionsTarget && (
        <StickingActionsDialog
          open={actionsOpen && !!actionsTarget}
          onOpenChange={closeActions}
          registration={actionsTarget}
        />
      )}*/}
    </div>
  );
}
